import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  assignUserRoles,
  createUser,
  issueTemporaryPassword,
  resetUserMfa,
  revokeDevice,
  setUserStatus,
  updateRolePermissions,
  updateUser,
} from '@/api/admin-api'
import type {
  AssignUserRolesRequest,
  CreateUserRequest,
  IssueTemporaryPasswordRequest,
  ResetUserMfaRequest,
  RevokeDeviceRequest,
  SetUserStatusRequest,
  UpdateRolePermissionsRequest,
  UpdateUserRequest,
} from '@/api/admin-types'

import { adminKeys } from './admin-keys'

/**
 * Commands over the administration module.
 *
 * Every one of these except issuing a temporary password answers with a {@link DualControlResult}
 * or with nothing (`revokeDevice`), never with the changed record, so nothing is written into the
 * cache directly — `groups/mutations.ts` explains the same choice for the same reason: when a
 * request is awaiting a countersignature there is no changed row to write, and invalidating is
 * correct either way, because it re-reads and finds whichever of the two is now true.
 */

function invalidateUser(queryClient: QueryClient, userId: string) {
  void queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) })
  void queryClient.invalidateQueries({ queryKey: adminKeys.userLists() })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateUserRequest) => createUser(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.userLists() })
    },
  })
}

export function useUpdateUser(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateUserRequest) => updateUser(userId, request),
    onSuccess: () => {
      invalidateUser(queryClient, userId)
    },
  })
}

export function useSetUserStatus(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SetUserStatusRequest) => setUserStatus(userId, request),
    onSuccess: () => {
      invalidateUser(queryClient, userId)
    },
  })
}

export function useResetUserMfa(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ResetUserMfaRequest) => resetUserMfa(userId, request),
    onSuccess: () => {
      invalidateUser(queryClient, userId)
    },
  })
}

/**
 * Not invalidated on success: the password is shown once, from the response itself, and re-reading
 * the user changes nothing about it — the server does not echo it back on a later read, because it
 * does not store it in the clear.
 */
export function useIssueTemporaryPassword(userId: string) {
  return useMutation({
    mutationFn: (request: IssueTemporaryPasswordRequest) => issueTemporaryPassword(userId, request),
  })
}

export function useAssignUserRoles(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AssignUserRolesRequest) => assignUserRoles(userId, request),
    onSuccess: () => {
      invalidateUser(queryClient, userId)
    },
  })
}

export function useUpdateRolePermissions(roleId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateRolePermissionsRequest) => updateRolePermissions(roleId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.roles() })
    },
  })
}

/**
 * Revokes a field device from a user's account. Plain 204 on success — see `admin-api.ts` on why
 * this is not treated as a dual-controlled command despite the permission catalogue's
 * `sync.device.revoke.approve`.
 */
export function useRevokeDevice(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, ...request }: RevokeDeviceRequest & { deviceId: string }) =>
      revokeDevice(deviceId, request),
    onSuccess: () => {
      invalidateUser(queryClient, userId)
    },
  })
}
